import yaml from "js-yaml";

import type { DataSchedule } from "./dataDelay.mjs";

/**
 * Manifest schema mirrors `customers/<slug>/dashboard_manifest.yaml`.
 * Source of truth lives in the pipeline repo. This module reads the
 * YAML at build time (Phase 4) or runtime (Phase 5 drill-down) and
 * exposes a typed view to the renderer.
 *
 * Only the fields the renderer consumes are declared here. The
 * pipeline-side schema is richer (status, prebuilt_report, etc.).
 */

export type ChartType =
  | "single_number"
  | "kpi_comparison"
  | "bar"
  | "stacked_bar"
  | "line"
  | "line_area"
  | "pie"
  | "pivot"
  | "tabular"
  | "html_strip"
  // AR Aging tab (wave 1): a self-contained panel computed client side from
  // the newest ar_current snapshot by arAging.mjs, rendered by ArAgingWidget.
  | "ar_aging"
  // RO Efficiency tab: a panel drawn by RoEfficiencyPanel from service_event rows.
  | "ro_efficiency"
  // Containers tab (wave 1, the client's Container Inventory tool): one
  // renderer, several views, all cut from one grouped read.
  | "container_inventory"
  // Revenue tab (wave 2, the client's revenue dashboard): one renderer
  // (RevenueTabWidget), a view per widget, all cut from grouped invoice reads.
  | "revenue_tab";

/** The AR Aging tab's panels, one per widget (see templates L1c.*). */
export type ArAgingView =
  | "bucket_tiles"
  | "segment_panels"
  | "distribution"
  | "status_counts"
  | "risk_top"
  | "warning"
  | "critical"
  | "bucket_pages"
  | "all_accounts";

/**
 * Revenue tab (wave 2): a widget's view and the customer's rules as
 * resolve-manifest.mjs applyRevenueTabConfig stamped them. The template writes
 * only `{view, group}`; the resolver adds the category groups (from the
 * customer's mappings.yaml revenue_category_rules), fuel surcharge names,
 * credit exclusions, working-day rule and fuel surcharge proposal (from
 * config.yaml revenue_tab). revenueTab.mjs normalizeRevenueConfig reads it.
 */
export interface RevenueTabBlock {
  view: string;
  group?: string | null;
  groups?: Record<string, unknown>;
  fuel_surcharge_names?: string[];
  fsc_proposal?: { floor_share: number; step_share: number } | null;
  working_days?: "weekdays" | null;
  credit_exclusions?: string[];
  division_labels?: Record<string, string>;
  /** The customer's divisions in config order (tables and charts follow it). */
  division_order?: string[];
}

/**
 * RO Efficiency tab: a panel's resolved rules (resolve-manifest.mjs
 * applyRoEfficiencyConfig, from customers/<slug>/config.yaml ro_efficiency).
 * The template writes only `{panel, line}`; the resolver fills the rest.
 */
export interface RoEfficiencyConfig {
  panel: "stats" | "driver_grid" | "division_grid" | "driver_totals" | "fel_drivers" | "scheduled";
  line: string;
  line_config: {
    haul_work_types: string[];
    delivery_work_types: string[];
    working_day_min_hauls: number | null;
  };
  excluded_route_prefixes: string[];
  route_rows: { label: string; routes: string[] }[];
  fel: { delivery_work_types: string[]; removal_work_types: string[] };
  goals: "awaiting";
  /** Display name per lowercase canonical division id, from customer config
   * `division_labels`; absent or empty means title-case the id. */
  division_labels?: Record<string, string>;
}

export type Tier = "essential" | "growth" | "professional" | "enterprise";

export type KpiSubtype = "single_number" | "snapshot" | "kpi_comparison";

export type Aggregation =
  | "sum"
  | "avg"
  | "min"
  | "max"
  | "count"
  | "distinct_count"
  | "latest"
  // MER-560: count(add-overlay rows) minus count(subtract-overlay rows),
  // both sides sharing the widget's base filter/window. See ManifestWidget.net.
  | "net_count"
  // MER-560 part 2: count(numerator overlay) / count(denominator overlay),
  // both sides sharing the widget's base filter/window. Returns a FRACTION
  // (house convention for percent columns). See ManifestWidget.ratio.
  | "ratio_count";

export interface WidgetSource {
  kind: "table" | "view" | "report" | "cqt";
  name: string;
}

export interface WidgetField {
  column: string;
  aggregation?: Aggregation;
}

/**
 * KPI-comparison widgets (1.3.1 FL P&L, 1.3.2 RO P&L, 1.3.3 SL P&L on the
 * DWS manifest) declare three side-by-side panes instead of a single
 * `column`. Each pane is either a column+aggregation or a simple
 * arithmetic expression composed of aggregations on the same source.
 */
export interface KpiComparisonPane {
  column?: string;
  aggregation?: Aggregation;
  expression?: string;
  label: string;
  // Optional per-measure / per-pane format override (MER-180). When
  // omitted, the renderer infers from column / label naming and falls
  // back to the widget-level display_format.
  display_format?: string;
}

export interface KpiComparisonField {
  primary?: KpiComparisonPane;
  secondary?: KpiComparisonPane;
  tertiary?: KpiComparisonPane;
}

export function isKpiComparisonField(
  f: WidgetField | KpiComparisonField | undefined,
): f is KpiComparisonField {
  if (!f) return false;
  return "primary" in f || "secondary" in f || "tertiary" in f;
}

/**
 * Pivot / measure-table widgets (e.g. 1.2 Revenue by Operation on the
 * DWS manifest) declare a row dimension and an array of measures. Each
 * measure renders as a column with the aggregated value per row group.
 * Measure entries share the KpiComparisonPane shape: column+aggregation
 * or a simple arithmetic expression composed of aggregations.
 */
export type PivotMeasure = KpiComparisonPane;

/**
 * Chart series for type:chart widgets. Same shape as a pivot measure
 * plus an optional Chart.js color. `y_axis_series` is an array so a
 * single widget can stack multiple measures on the same x-axis.
 */
export interface ChartSeries extends KpiComparisonPane {
  color?: string;
  // MER-640: a per-series filter, merged over the widget filter for this
  // series alone. Without it a "distribution" chart can only bind the same
  // column once per series and draw one number in every bucket: RO Container
  // Idle Time declared five identical count(event_id) series and stacked
  // August roll-off five times over. Posted vs Unposted by LOB is the same
  // shape with two. The keys are the ordinary applyFilters clauses, so a
  // bucket is `{is_posted: 1}` or `{idle_days: {op: lt, value: 7}}`.
  filter?: Record<string, WidgetFilterClause | string | number | boolean>;
}

/**
 * Every filter op `applyFilters` understands.
 *
 * MER-589: this union used to omit `is_not_null`, `latest` and `lte` while all
 * three were live in the resolved manifests. Manifests are YAML parsed at
 * runtime and cast, so the union enforced nothing, and `applyFilters` switched
 * on the seven it knew with no default branch. An unmatched op therefore added
 * NO predicate and the query ran unfiltered: 74 clauses across 20 widgets on
 * both tenants were silently dropped. `applyFilters` now throws on an
 * unrecognised op, and scripts/manifest-ops.test.mjs pins every op used in the
 * manifests to this list, so the next typo fails the build instead of quietly
 * widening a query.
 */
export type WidgetFilterOp =
  | "in"
  | "eq"
  | "neq"
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "isnull"
  | "notnull"
  | "is_not_null"
  | "latest"
  // MER-620: the complement of `in`. The AR commercial and residential tiles
  // have to PARTITION the portfolio: whatever is not on a residential bill
  // group is commercial, so the two tiles add to the total by construction and
  // a bill group Navusoft adds tomorrow lands in one of them rather than
  // falling out of both.
  | "not_in";

export interface WidgetFilterClause {
  op: WidgetFilterOp;
  value?: string | number | boolean | (string | number | boolean)[];
}

export interface WidgetTimeline {
  column: string;
  range?: string;
  mode?: string;
}

export interface WidgetGrid {
  span?: number;
  row?: number;
}

export interface ManifestWidget {
  id: string;
  name: string;
  tab: string;
  /** MER-504: rendered on the widget face when an active global filter
   * does not apply to it (e.g. "Company-wide" under a division
   * filter). Set by applyGlobalFiltersToWidget, never by manifests. */
  scope_note?: string;
  /** MER-588: the tenant's newest snapshot period (null when it has none),
   * resolved by resolveLatestFilters for widgets pinning
   * `period_type: snapshot` and applied as a row restriction by applyFilters.
   * Never set by manifests. */
  snapshot_period?: string | null;
  // Manifest uses extra aliases for backward-compat with the Zoho era.
  // effectiveChartType maps them onto the canonical ChartType union.
  type?:
    | "kpi"
    | "chart"
    | "table"
    | "tabular"
    | "pivot"
    | "pivot_table"
    | "html_strip"
    | "html"
    | "header_strip"
    | "ar_aging"
    | "ro_efficiency"
    | "container_inventory"
    | "revenue_tab";
  /** Revenue tab view and rules; see RevenueTabBlock. */
  revenue_tab?: RevenueTabBlock;
  /** RO Efficiency tab panel rules; see RoEfficiencyConfig. */
  ro_efficiency?: RoEfficiencyConfig;
  chart_type?: ChartType;
  /** AR Aging tab (wave 1): which of Zay's panels this widget is, and the
   * customer's residential rule as resolve-manifest.mjs stamped it (null when
   * the customer declared none). */
  ar_aging?: {
    view: ArAgingView;
    segment_rule?: {
      label: string;
      bill_groups: string[];
      bill_group_contains: string[];
    } | null;
  };
  // Manifest chart widgets actually declare their visual via chart_subtype
  // (bar / stacked_bar / line / line_area / pie). chart_type is mostly the
  // post-resolution field; chart_subtype is what authors write.
  chart_subtype?: "bar" | "stacked_bar" | "line" | "line_area" | "pie";
  kpi_subtype?: KpiSubtype;
  source?: WidgetSource;
  field?: WidgetField | KpiComparisonField;
  /** MER-545: single_number tiles opting in get a second query over the
   * adjacent prior window (priorWindow.mjs) and render the KPITile
   * "+X.X% vs prior" delta. Snapshot-mode widgets have no prior window
   * and never show a delta regardless of this flag. */
  trend?: "vs_prior_period";
  // For type: pivot_table widgets, the manifest declares the row axis
  // and a list of measures (instead of `field`). columns are inferred
  // from the measure labels when omitted; an explicit column axis would
  // create a true 2D pivot (not yet used in DWS).
  rows?: string;
  // For pivot_table widgets, the (optional) column axis (a single column name).
  // MER-438: for `tabular` widgets, `columns` is instead the ORDERED column
  // allow-list -- only these render, so internal plumbing (tenant_id, period
  // machinery) never reaches the customer. Hence the string[] member.
  columns?: string | string[] | null;
  // MER-438: per-column presentation for tabular widgets. Sensible defaults
  // (humanized header, inferred format, right-aligned numerics) apply when a
  // column is omitted; override only where the default is wrong.
  column_overrides?: Record<string, { label?: string; format?: string; align?: "left" | "right" }>;
  measures?: PivotMeasure[];
  // For type: chart widgets, manifest declares the dimension axis and an
  // array of measure series. Each ChartSeries becomes a Chart.js dataset.
  x_axis?: string;
  y_axis_series?: ChartSeries[];
  // MER-462: pie widgets declare a slice dimension + one aggregated
  // measure (e.g. L1b.2 Payment Method Mix: slice_by Payment_Method_Clean,
  // value sum(Amount)). The renderer maps this onto the same grouped-
  // aggregate path as x_axis + y_axis_series. Before this existed the
  // shape was silently unimplemented and the widget fell through to the
  // raw-rows path, which rendered a bare axis frame.
  slice_by?: string;
  value?: { column: string; aggregation: Aggregation };
  filter?: Record<string, WidgetFilterClause | string | number | boolean>;
  /** MER-560: for `aggregation: net_count` tiles. Each side is a filter
   * overlay merged onto the widget's base `filter`; the tile renders
   * count(add) - count(subtract). The Overview net-container tiles keep
   * these overlays identical to their sibling delivery/removal tiles'
   * filters (asserted in overview-tab.test.mjs), so the net reconciles
   * with those tiles by construction. */
  net?: {
    add?: Record<string, WidgetFilterClause | string | number | boolean>;
    subtract?: Record<string, WidgetFilterClause | string | number | boolean>;
  };
  /** MER-560 part 2: for `aggregation: ratio_count` tiles. Each side is a
   * filter overlay merged onto the widget's base `filter`; the tile renders
   * count(numerator) / count(denominator) as a fraction. The Swap % tile's
   * numerator work_type list is per-customer, injected at resolve time from
   * customers/<slug>/config.yaml `swap_pct.work_types`. */
  ratio?: {
    numerator?: Record<string, WidgetFilterClause | string | number | boolean>;
    denominator?: Record<string, WidgetFilterClause | string | number | boolean>;
  };
  /** MER-560 part 2: template marker telling resolve-manifest.mjs to inject
   * the per-customer swap_pct config (work_types, target, floor note) into
   * this widget. Stripped at resolve time; never reaches the renderer. */
  swap_pct_config?: boolean;
  /** MER-560 part 2: owner-entered goal, injected at resolve time from
   * customer config. The renderer flags the measured value against it
   * (GREEN at/above target, YELLOW within 80%, RED below, for
   * higher_is_better; mirrored otherwise). The measured value itself is
   * never editable. */
  target?: {
    value: number;
    direction?: "higher_is_better" | "lower_is_better";
  };
  /** MER-605: horizontal target lines on a chart widget. Mirrors the
   * pipeline-side `ReferenceLine` model (dashboard/manifest.py). `value` is
   * authored in DISPLAY units, the number the y-axis tick prints, so
   * `{value: 100, label: Break-even}` on a percent chart means the 100%
   * gridline even though the underlying column stores 1.0. ChartWidget
   * converts via resolveReferenceLines() and Chart.tsx draws them. */
  reference_lines?: { value: number; label?: string }[];
  /** MER-625: the manifest id of the widget this tile drills into -- the
   * detail layer for the summary number, Operations for the Overview. A
   * click NAVIGATES to that widget's tab with the page's filter state
   * carried in the URL; nothing is recomputed on the way, so a drill cannot
   * restate a ratio differently from its tile (MER-715). Only authorable
   * between two widgets whose data binding is identical, which
   * scripts/drill-target.test.mjs asserts per template and per resolved
   * tenant manifest. Resolution and the fail-closed gates live in
   * drillTarget.mjs. */
  drill_to?: string;
  /** Ticket reference rendered with the blocked state (e.g. "MER-560"). */
  blocked_by?: string;
  /** MER-717: for a `status: blocked` tile with no column to bind yet, the
   * vendor-neutral client feed it waits on ("a fleet register"). The tile
   * reads "Not available - awaiting <feed>" (missingFeed.mjs
   * declaredFeedGap), and resolve-manifest.mjs keeps the widget instead of
   * dropping it as pending: the gap is the customer's to close, like a
   * connector gate, not unbuilt work of ours. */
  awaiting_feed?: string;
  /** Containers tab: which view of the Container Inventory this widget is
   * (kpi, inventory_snapshot, monthly_summary, ...). Read only by
   * ContainerInventoryWidget. */
  view?: string;
  /** Containers tab: the metric a `view: kpi` tile shows (ro_delivery ...). */
  container_metric?: string;
  /** Containers tab: the line a per-line view covers (roll_off / front_load). */
  container_lob?: string;
  /** Containers tab: the customer's work types per line, injected at resolve
   * time from config.yaml `container_inventory` (MER-819: never a template
   * literal). Absent on a template; the resolver blocks the widget instead. */
  container_inventory?: Record<string, unknown>;
  /** MER-717: copy for a live single-number tile whose query legitimately
   * returns nothing, where the generic "Try a wider range" would mislead
   * (a year-over-year figure before a year of history exists). */
  empty_copy?: string;
  /** MER-717: false opts a tile out of BENCHMARK/FLAG colouring. Board tiles
   * carry no band derived from our own history. */
  benchmark_flags?: boolean;
  timeline?: WidgetTimeline;
  grid?: WidgetGrid;
  display_format?: string;
  // MER-465 PR B: opt-out of the MER-317 "count of 0 renders the
  // limited-activity copy" rule for tiles where zero is a real, desirable
  // measurement (e.g. L4.3c Routes LOW Count).
  count_zero_is_data?: boolean;
  // MER-441: sane display range. A value outside [min, max] renders the
  // "Pending: insufficient source data" state instead of an impossible figure
  // (e.g. $46,454/ton from a near-zero tons denominator). Percent widgets also
  // gate beyond +/-200% automatically. Bounds are in the column's stored units
  // (dollars for $/ton; fractions for *_pct, where 0.9 == 90%).
  display_bounds?: { min?: number; max?: number };
  what_it_shows?: string;
  // MER-604: the technical definition behind the number -- the source
  // columns, the formula, the scope caveat. Sander's 2026-03-08 ask.
  // Rendered behind an info control in the tile chrome, never on the face of
  // the card (that slot is what_it_shows). The raw manifest value doubles as
  // our build log, so it goes through clientNote() first, which strips ticket
  // and PR references. 66 DWS widgets and 64 Best Disposal widgets carry one.
  widget_note?: string;
  /**
   * MER-558: the other two thirds of AJ's KPI standard, beside `widget_note`.
   *
   * `widget_note` answers "how is this measured". These answer "what do I do
   * when it moves" and "what does good look like". All three render as
   * sections of the one tile-info panel, and the panel's opening section is
   * `what_it_shows` itself, so the caption on the face of the card and the
   * definition behind it come from a single manifest field and cannot drift.
   *
   * Both go through clientNote() like every other panel string, so an
   * internal reference cannot reach a customer through a new field.
   */
  action?: string;
  good?: string;
  // MER-292: optional sales-facing caption shown only in demo mode
  // (?demo=1). Operator-facing what_it_shows still wins outside demo.
  sales_caption?: string;
  why?: string;
  // MER-694: not_offered is stamped by resolve-manifest (never authored) on a
  // widget whose equipment_type scope shares no line with enabled_services.
  // The customer view drops it (MER-577); only a show_pending_widgets build
  // ships and renders it.
  status?: "ok" | "blocked" | "deferred" | "suppressed" | "not_offered";
  enabled_for?: string[];
  // MER-306 close: widgets carrying `service_gate: <lob>` (e.g.
  // `service_gate: asl`) only render for customers whose
  // enabled_services include that LOB. Pipeline data may exist for
  // every LOB but a customer with zero invoices in that line
  // (BD has zero ASL revenue) would otherwise render the per-LOB
  // P&L card as a bare n/a. CustomerDashboard filters widgets on
  // this field at the same point it applies global filters.
  service_gate?: string;
  /**
   * MER-739 / MER-629: what this widget's line-of-business scope MEANS, stated
   * rather than guessed from the shape of a list.
   *
   * Three values, distinguishable by type:
   *
   *   "all_enabled"  the widget's `filter.equipment_type` in-list is the
   *                  platform rollup, to be rewritten to the tenant's
   *                  enabled_services at resolve time. resolve-manifest.mjs
   *                  used to infer this from the list being exactly
   *                  [front_load, roll_off, asl], so a DELIBERATE scope that
   *                  happened to name those three (to exclude hook_lift, say)
   *                  was silently widened.
   *   "fixed"        the in-list is a deliberate scope: narrowed to what the
   *                  tenant enables, never widened.
   *   string[]       the widget declares NO equipment_type filter and its
   *                  population is already restricted to these lines by other
   *                  means: strict per-source work_type literals, a KPI
   *                  Summary column computed for one line only, or a
   *                  customer-specific work-type list. The page LOB chip then
   *                  leaves the tile alone instead of injecting a second,
   *                  conflicting LOB filter (MER-629), and lobScopeNote labels
   *                  its face.
   */
  lob_scope?: "all_enabled" | "fixed" | string[];
  /**
   * MER-819: the governed metric this customer has NOT defined, stamped by
   * resolve-manifest from customers/<slug>/ontology.yaml. Never authored in a
   * template: no template knows how a particular hauler counts.
   *
   * Its presence gates the tile with DEFINITION_GATE_COPY instead of
   * GATE_COPY. Both are blanks; they blame different things, and only one of
   * them is true at a time. "Awaiting source data" tells an owner their
   * upload has not landed and sends them to look at a feed that is working.
   * "Awaiting definition" tells them we have not asked how they count it,
   * which is our move to make, not theirs.
   */
  awaiting_definition?: string;
  /**
   * Wave 1 Disposal tab. All opt-in; a widget without them reads and renders
   * as before.
   *
   * `row_limit` / `order_by`: a tabular read returns exactly this many rows in
   * this order, plus the true match count, so a ticket list over thousands of
   * rows is capped on purpose and says so instead of being truncated silently
   * by PostgREST (queries.ts rowLimitOf).
   *
   * `rows_then`, `sort`, `limit`: a measure pivot's second grouping column,
   * row order (by a measure label, or `key_direction: desc`) and top-N cap
   * (lib/pivotShape.mjs).
   */
  row_limit?: number;
  order_by?: { column: string; direction?: "asc" | "desc" };
  rows_then?: string;
  row_labels?: Record<string, string>;
  sort?: {
    measure?: string;
    direction?: "asc" | "desc";
    key_direction?: "asc" | "desc";
    custom_order?: string[];
    column?: string;
  };
  limit?: number;
}

export interface ManifestTab {
  id?: string;
  label?: string;
  layer?: string;
  tier?: Tier;
  /** MER-545: short uppercase pill rendered next to the nav label
   * (e.g. "NEW" on the Overview tab while it is fresh). */
  badge?: string;
  /** MER-688: the tab is for Meridian staff only (impersonating or not).
   * A client session never sees it in the nav and gets a "staff only"
   * notice on its URL. This is a UI gate layered on top of RLS, never a
   * data gate: the manifest is a public file, so hiding a tab here hides
   * nothing a client could not already read. Independent of `tier`,
   * which still governs the lock badge for the tabs a client can see. */
  staff_only?: boolean;
  /** Set only by a customer's dashboard_overrides.yaml (tab_overrides): this
   * tab is the customer's Executive view. The shell then shows no separate
   * Executive workbook link, the bare URL lands here, and /<slug>/Executive
   * redirects here (navSections.mjs executiveTabId). */
  executive?: boolean;
  /** AJ 2026-09-28: the tab is one of the client's own reports, rebuilt as a
   * React port of the client's HTML page (dashboard/src/reports/). The id
   * names the port in the report registry. A registered id renders that
   * page instead of the section grid; an unregistered id falls back to the
   * grid, so a template may name a report before its port lands. */
  report?: string;
}

export interface DashboardManifest {
  /** The resolver's customer id (config customer_id); older bundles only. */
  customer?: string;
  /** Stamped by resolve-manifest.mjs reportIdentity from config.yaml. */
  customer_slug?: string;
  customer_name?: string;
  /** config short_name when stated; the ports otherwise use the name's initials. */
  customer_short_name?: string;
  /** config division_labels: lowercase division id -> display name. */
  division_labels?: Record<string, string>;
  /** The customer's own systems as the report ports name them
   * (dispatch, time_clock, incidents). Absent: neutral wording. */
  source_labels?: Record<string, string>;
  /** config route_profitability (#1090): where Route Profitability's hours
   * come from. Absent: driver_timecard (the time clock). Stamped by
   * resolve-manifest.mjs routeProfitabilityBlock. */
  route_profitability?: { hours_source?: "driver_timecard" | "route_day" };
  tier?: Tier;
  tabs?: ManifestTab[];
  widgets: ManifestWidget[];
  /** MER-163: divisions for the Division global-filter chips.
   * Mirrored from customer config.yaml `divisions:`. */
  divisions?: string[];
  /** MER-163: enabled services (front_load / roll_off / asl) for the
   * LOB global-filter chips. Mirrored from config.yaml
   * `enabled_services:`. */
  enabled_services?: string[];
  /** Set only by a customer's dashboard_overrides.yaml (`executive_page:
   * derived`): the Executive page renders the Meridian-derived board
   * (public.exec_derived) for a tenant with no workbook. The shell shows the
   * Executive link and lands there. Absent for every other tenant. */
  executive_page?: "derived";
  /** 2026-10-05: when each scheduled feed is expected (the tenant's
   * navusoft_portal lanes and daily export) and how late it may run
   * (config.yaml data_freshness), stamped by resolve-manifest.mjs from
   * dataDelay.mjs dataScheduleFromConfig. Drives the "Data delayed"
   * banner. Absent for a tenant with no Navusoft schedule. */
  data_schedule?: DataSchedule;
  /** Issue #1096: the in-app help desk, from config.yaml `help_desk`.
   * Present (enabled: true) only when the tenant has it switched on. */
  help_desk?: { enabled: boolean };
}

/**
 * MER-214: typed error so the renderer can distinguish a missing
 * manifest (route to "not configured yet" card) from a corrupt one
 * (route to "configuration error" card) without ever showing the raw
 * exception text to a customer.
 */
export class ManifestParseError extends Error {
  constructor(
    message: string,
    /** Underlying YAML / parse cause for the console log. Not surfaced
     * to the client. */
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = "ManifestParseError";
  }
}

export function parseManifest(yamlText: string): DashboardManifest {
  // Empty or whitespace-only payload: treat as a parse error so the
  // caller renders the configuration-error card. An empty manifest
  // would render an empty dashboard, which is more confusing than a
  // clear error state.
  if (yamlText.trim().length === 0) {
    throw new ManifestParseError("Manifest is empty");
  }
  let parsed: unknown;
  try {
    parsed = yaml.load(yamlText);
  } catch (err) {
    throw new ManifestParseError("Manifest YAML is invalid", err);
  }
  if (parsed === null || parsed === undefined) {
    throw new ManifestParseError("Manifest YAML parsed to null");
  }
  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new ManifestParseError(
      `Manifest YAML root must be a mapping, got ${Array.isArray(parsed) ? "array" : typeof parsed}`,
    );
  }
  return parsed as DashboardManifest;
}

/**
 * Heuristic: did this response body look like the Cloudflare Pages SPA
 * fallback (HTML index.html served when no real manifest exists for the
 * requested slug)?
 *
 * Two signals, either is sufficient:
 *   * Content-Type contains "html".
 *   * Body opens with `<!doctype` or `<html` after stripping whitespace.
 *
 * Returning true means "treat this slug as not-yet-configured" rather
 * than running it through the YAML parser (which would mangle HTML
 * into a parse error and bury the real cause).
 */
export function looksLikeSpaFallback(
  contentType: string | null | undefined,
  body: string,
): boolean {
  const ct = (contentType ?? "").toLowerCase();
  if (ct.includes("html")) return true;
  const stripped = body.trimStart().slice(0, 200).toLowerCase();
  return stripped.startsWith("<!doctype") || stripped.startsWith("<html");
}

export function widgetsForTab(manifest: DashboardManifest, tab: string): ManifestWidget[] {
  return (manifest.widgets ?? []).filter((w) => w.tab === tab);
}

/**
 * Effective chart_type for a widget. The manifest writers use richer
 * vocabulary than the renderer (chart_subtype, type aliases like
 * `tabular`/`pivot_table`/`header_strip`/`html`, kpi_subtype:
 * `kpi_comparison`); this maps everything onto the single ChartType union
 * the renderer's switch statements understand.
 */
export function effectiveChartType(widget: ManifestWidget): ChartType | undefined {
  if (widget.chart_type) return widget.chart_type;

  // KPI dispatch. kpi_comparison is a 3-pane tile, single_number is a scalar.
  if (widget.type === "kpi") {
    if (widget.kpi_subtype === "kpi_comparison") return "kpi_comparison";
    return "single_number";
  }
  if (widget.kpi_subtype === "single_number" || widget.kpi_subtype === "snapshot") {
    return "single_number";
  }
  if (widget.kpi_subtype === "kpi_comparison") return "kpi_comparison";

  // Chart dispatch reads chart_subtype, not chart_type. Fallback to bar for
  // chart widgets that didn't declare a subtype (L4.6 / L4.7).
  if (widget.type === "chart") {
    const sub = widget.chart_subtype;
    if (sub === "bar" || sub === "stacked_bar" || sub === "line" || sub === "pie") return sub;
    if (sub === "line_area") return "line_area";
    return "bar";
  }

  // Type aliases. The manifest uses tabular / pivot_table / header_strip /
  // html alongside table / pivot / html_strip; canonicalize.
  if (widget.type === "tabular" || widget.type === "table") return "tabular";
  if (widget.type === "pivot_table" || widget.type === "pivot") return "pivot";
  if (widget.type === "html_strip" || widget.type === "html" || widget.type === "header_strip") {
    return "html_strip";
  }
  if (widget.type === "ar_aging") return "ar_aging";
  if (widget.type === "ro_efficiency") return "ro_efficiency";
  if (widget.type === "container_inventory") return "container_inventory";
  if (widget.type === "revenue_tab") return "revenue_tab";

  return undefined;
}

export function tabsFromManifest(manifest: DashboardManifest): string[] {
  if (manifest.tabs && manifest.tabs.length > 0) {
    return manifest.tabs.map((t) => t.id ?? t.label ?? "").filter((s) => s.length > 0);
  }
  const seen = new Set<string>();
  for (const w of manifest.widgets ?? []) {
    if (w.tab) seen.add(w.tab);
  }
  return Array.from(seen);
}
